import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw, CheckCircle, AlertTriangle, Download, Trash2 } from 'lucide-react';

const PaymentReconcileBanner = ({ API_URL, onDone }) => {
  const [running, setRunning] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runDedupe = async () => {
    if (!window.confirm(
      "Remove duplicate payment records?\n\n" +
      "This scans for subscriptions that share the same Razorpay Payment ID (true duplicates) " +
      "and keeps only one record per payment. This first runs a preview.")) return;
    setDeduping(true);
    try {
      // Dry run first
      const dry = await fetch(`${API_URL}/api/admin/subscriptions/dedupe-payments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: true })
      });
      const dryData = await dry.json();
      if (!dry.ok) { window.alert('Dedupe preview failed: ' + (dryData.detail || 'error')); return; }
      if (dryData.records_to_delete === 0) { window.alert('✅ No duplicate payments found. Nothing to clean.'); return; }
      if (!window.confirm(
        `Found ${dryData.duplicate_groups} duplicated payment(s) → ${dryData.records_to_delete} extra record(s) will be permanently removed (one copy of each is kept).\n\nProceed?`
      )) return;
      const res = await fetch(`${API_URL}/api/admin/subscriptions/dedupe-payments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: false })
      });
      const data = await res.json();
      if (res.ok) {
        window.alert(`✅ ${data.message}`);
        if (typeof onDone === 'function') onDone();
      } else {
        window.alert('Dedupe failed: ' + (data.detail || 'error'));
      }
    } catch (err) {
      window.alert('Dedupe error: ' + err.message);
    } finally {
      setDeduping(false);
    }
  };

  const runReconcile = async () => {
    const daysStr = window.prompt(
      "Recover payments that succeeded in Razorpay but never reached this admin panel " +
      "(due to failed webhook deliveries).\n\n" +
      "Scan how many days back?\n\n" +
      "Recommended: 30 (max: 180)",
      "30"
    );
    if (!daysStr) return;
    const days = parseInt(daysStr, 10);
    if (!days || days < 1) {
      window.alert('Enter a positive number of days.');
      return;
    }
    if (!window.confirm(`Scan Razorpay for all successful payments in the last ${days} day(s) and import missing ones? This is safe to run multiple times.`)) return;

    setRunning(true);
    setLastResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/razorpay/reconcile-payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setLastResult(data);
        if (typeof onDone === 'function') onDone();
      } else {
        window.alert('Reconciliation failed: ' + (data.detail || data.message || 'unknown error'));
      }
    } catch (err) {
      window.alert('Reconciliation error: ' + err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="mb-4 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Download className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">Recover missing payments from Razorpay</p>
              <p className="text-xs text-gray-600 mt-0.5">
                Fetches all captured payments &amp; charged autopay subscriptions from Razorpay
                and imports any that aren&apos;t already in your admin (e.g. missed due to webhook delivery
                failures). Safe to run repeatedly — existing records are skipped.
              </p>
              {lastResult && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 border border-green-300 rounded-full px-2 py-0.5">
                    <CheckCircle className="w-3 h-3" />
                    {lastResult.total_new_records} new record{lastResult.total_new_records === 1 ? '' : 's'} created
                  </span>
                  <span className="bg-white border border-gray-200 rounded-full px-2 py-0.5 text-gray-700">
                    One-time: {lastResult.one_time_created}/{lastResult.one_time_scanned} scanned
                  </span>
                  <span className="bg-white border border-gray-200 rounded-full px-2 py-0.5 text-gray-700">
                    Autopay: {lastResult.autopay_created}/{lastResult.autopay_scanned} scanned
                  </span>
                  <span className="bg-white border border-gray-200 rounded-full px-2 py-0.5 text-gray-700">
                    Last {lastResult.days_scanned} day(s)
                  </span>
                  {lastResult.errors && lastResult.errors.length > 0 && (
                    <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5">
                      <AlertTriangle className="w-3 h-3" />
                      {lastResult.errors.length} warning{lastResult.errors.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={runReconcile}
            disabled={running || deduping}
            className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
            data-testid="reconcile-razorpay-btn"
          >
            {running ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Scanning…
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Reconcile from Razorpay
              </>
            )}
          </Button>
          <Button
            onClick={runDedupe}
            disabled={running || deduping}
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50 flex-shrink-0"
            data-testid="dedupe-payments-btn"
          >
            {deduping ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Cleaning…</>
            ) : (
              <><Trash2 className="w-4 h-4 mr-2" /> Remove duplicates</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentReconcileBanner;
